/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import Axios, {
  AxiosRequestConfig,
  AxiosPromise,
  Canceler,
  AxiosError,
} from "axios";

//custom libraries
import { useCSRValue, useMessageValue } from "../../context";
import { SKIP_MESSAGE_SNACKBAR } from "../../constants/message";
import { useError } from "../use-error";
import { API_URL } from "../../constants/common";
import * as ACTIONS from "../../constants/actions";

export const useRestService = () => {
  const { csr, dispatch } = useCSRValue();
  const { setMessage } = useMessageValue();
  const { transformError } = useError();

  const handleSessionError = (error: AxiosError) => {
    if (error.isAxiosError) {
      const errorResponse = error.response;
      const config: any = error.config;
      if (errorResponse) {
        if (errorResponse.status === 401) {
          dispatch({
            type: ACTIONS.EXPIRED_CSR_ACTION,
            payload: {
              expired: true,
            },
          });
        }
        // const e = errorResponse.data.errors[0];
      } else if (!config[SKIP_MESSAGE_SNACKBAR]) {
        //handle all other errors
        setMessage(transformError(error));
      }
    }
    return Promise.reject(error);
  };

  const populateRequestHeader = (request: AxiosRequestConfig) => {
    let { headers } = request;
    if (!headers) {
      headers = {};
      request.headers = headers;
    }
    if (!headers["Authorization"]) {
      const { token } = csr;
      if (token) {
        Object.assign(headers, {
          Authorization: `Bearer ${token}`,
        });
      }
    }
  };
  const executeRequest = (
    request: AxiosRequestConfig
  ): [AxiosPromise<any>, Canceler[]] => {
    const cancels: Canceler[] = [];
    request.cancelToken = new Axios.CancelToken(function executor(c) {
      cancels.push(c);
    });
    request.url = `${API_URL}${request.url}`;
    populateRequestHeader(request);
    return [Axios(request).catch((e) => handleSessionError(e)), cancels];
  };

  const executeV2Request = (
    request: AxiosRequestConfig
  ): [AxiosPromise<any>, Canceler[]] => {
    const cancels: Canceler[] = [];
    request.cancelToken = new Axios.CancelToken(function executor(c) {
      cancels.push(c);
    });
    request.url = `${process.env.REACT_APP_TRANSACTION_V2_CONTEXT}${request.url}`;
    populateRequestHeader(request);
    return [Axios(request).catch((e) => handleSessionError(e)), cancels];
  };

  const executeSearchRequest = (
    request: AxiosRequestConfig
  ): [AxiosPromise<any>, Canceler[]] => {
    const cancels: Canceler[] = [];
    request.cancelToken = new Axios.CancelToken(function executor(c) {
      cancels.push(c);
    });
    request.url = `${process.env.REACT_APP_TRANSACTION_SEARCH}${request.url}`;
    populateRequestHeader(request);
    return [Axios(request).catch((e) => handleSessionError(e)), cancels];
  };

  return {
    executeRequest,
    executeV2Request,
    executeSearchRequest,
  };
};
